import { type ViewerBreadcrumbRoom, viewerBreadcrumb } from '@polygraf/shared'
import { type CSSProperties, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { cn } from '../ui'

/**
 * The viewer header's `space › room` line, each part a link to its own page
 * (see viewer-breadcrumb.ts in shared for why plain text was a dead end).
 *
 * `linked={false}` keeps the old plain-text line. The mobile app must use it:
 * its bundled screen viewer is a MemoryRouter with a single route, so a link
 * to `/spaces/…` renders nothing and leaves a blank WebView, and its cloud
 * transcript WebView has no web session to show the space page with. The
 * header's back arrow is hidden there for the same reason.
 *
 * Opacity sits on the text, not the container, and the focus ring is inset:
 * `truncate` clips overflow, and an outer or faded ring would be cut or
 * drop below 3:1.
 *
 * `wrap` lets a long room name run onto more lines instead of being cut off:
 * with the phone's largest text size even a short `space › room` overflows
 * one line in the transcript viewer (MU, 7. 10. 2026).
 */
export function ViewerBreadcrumb({
	room,
	linked,
	className,
	style,
	title,
	wrap = false,
}: {
	room: ViewerBreadcrumbRoom
	linked: boolean
	wrap?: boolean
	className?: string
	style?: CSSProperties
	title?: string
}) {
	const { t } = useTranslation()
	const crumbs = viewerBreadcrumb(room)
	const overflow = wrap ? 'break-words hyphens-auto' : 'truncate'

	if (!linked) {
		return (
			<div className={cn('text-sm opacity-70', overflow, className)} style={style} title={title}>
				{crumbs.map((crumb) => crumb.label).join(' › ')}
			</div>
		)
	}

	return (
		<nav
			aria-label={t('viewer.breadcrumb')}
			className={cn('text-sm', overflow, className)}
			style={style}
			title={title}
		>
			{crumbs.map((crumb, i) => (
				<Fragment key={crumb.href}>
					{i > 0 && (
						<span aria-hidden="true" className="opacity-70">
							{' › '}
						</span>
					)}
					<Link
						to={crumb.href}
						className="underline decoration-dotted underline-offset-2 hover:decoration-solid focus-visible:[outline-offset:-2px]"
					>
						<span className="opacity-70">{crumb.label}</span>
					</Link>
				</Fragment>
			))}
		</nav>
	)
}
